import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeedbackStatusEvent } from '@/entities/feedback';
import { formatDateTime } from '@/shared/lib/datetime';
import { useIsMobile } from '@/shared/lib/media';

import { statusLabel } from './labels';
import { StatusBadge } from './StatusBadge';
import { StatusIcon } from './StatusIcon';
import styles from './StatusTimeline.module.css';

interface Props {
  events: readonly FeedbackStatusEvent[];
  /** Moderators see who acted; the author sees "the team". */
  showActors: boolean;
  timeZone?: string | undefined;
}

/**
 * The persisted status history, oldest first. The CURRENT status carries the animated icon; past
 * entries are still. On a phone the full history is folded behind a button under a compact
 * current-status summary.
 */
export function StatusTimeline({ events, showActors, timeZone }: Props) {
  const { t, i18n } = useTranslation();
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const current = events.at(-1);
  if (!current) return null;
  const expanded = !mobile || open;
  return (
    <section className={styles.timeline} aria-label={t('feedback.timeline.events')}>
      <div className={styles.summary}>
        <StatusBadge status={current.to_status} animated size="lg" />
        <span className={styles.since}>
          {t('feedback.timeline.since', {
            when: formatDateTime(current.created_at, i18n.language, timeZone),
          })}
        </span>
      </div>
      {mobile && (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          {open
            ? t('feedback.timeline.hide')
            : t('feedback.timeline.show', { count: events.length })}
        </button>
      )}
      {expanded && (
        <ol className={styles.list} id={listId}>
          {events.map((event, index) => {
            const isCurrent = index === events.length - 1;
            const who = event.by_team
              ? showActors && event.actor
                ? event.actor.display_name
                : t('feedback.team')
              : null;
            return (
              <li
                key={event.id}
                className={`${styles.item} ${isCurrent ? styles.current : ''}`}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className={styles.marker}>
                  <StatusIcon status={event.to_status} animated={isCurrent} />
                </span>
                <div className={styles.body}>
                  <span className={styles.name}>
                    {statusLabel(t, event.to_status)}
                    {isCurrent && (
                      <span className={styles.now}>{t('feedback.timeline.current')}</span>
                    )}
                  </span>
                  <time className={styles.when} dateTime={event.created_at}>
                    {formatDateTime(event.created_at, i18n.language, timeZone)}
                  </time>
                  {who && <span className={styles.by}>{t('feedback.timeline.by', { who })}</span>}
                  {event.note && <p className={styles.note}>{event.note}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
